import React from 'react';

interface LoadingComponentProps {
  loadingText?: string;
  heading?: string;
  subheading?: string;
  bottomText?: string;
}

const LoadingComponent: React.FC<LoadingComponentProps> = ({
  loadingText = "Loading tests...",
  heading = "Preparing Your LSAT Practice Tests",
  subheading = "Please wait while we set up your test environment",
  bottomText = "This may take a few moments"
}) => {
  return (
    <div className="bg-gray-50 flex flex-col items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Animated SVG illustration */}
        <div className="relative h-48 mb-8">
          <svg className="w-full h-full" viewBox="0 0 240 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Books stack with animation */}
            <g className="animate-bounce" style={{ transformOrigin: 'center', animationDuration: '4s' }}>
              <rect x="20" y="70" width="60" height="10" rx="2" fill="#9061F9" />
              <rect x="25" y="60" width="50" height="10" rx="2" fill="#6366F1" />
              <rect x="30" y="50" width="40" height="10" rx="2" fill="#3B82F6" />
              <rect x="35" y="40" width="30" height="10" rx="2" fill="#0EA5E9" />
              <rect x="40" y="30" width="20" height="10" rx="2" fill="#22D3EE" />
              
              {/* Book titles */}
              <rect x="25" y="63" width="30" height="4" rx="1" fill="white" fillOpacity="0.8" />
              <rect x="32" y="53" width="20" height="4" rx="1" fill="white" fillOpacity="0.8" />
              <rect x="38" y="43" width="15" height="4" rx="1" fill="white" fillOpacity="0.8" />
              <rect x="42" y="33" width="10" height="4" rx="1" fill="white" fillOpacity="0.8" />
            </g>
            
            {/* Clock face */}
            <circle cx="125" cy="50" r="30" fill="#F3E8FF" stroke="#C084FC" strokeWidth="2" />
            <circle cx="125" cy="50" r="3" fill="#9333EA" />
            
            {/* Clock ticks */}
            <line x1="125" y1="20" x2="125" y2="24" stroke="#9333EA" strokeWidth="1" />
            <line x1="155" y1="50" x2="151" y2="50" stroke="#9333EA" strokeWidth="1" />
            <line x1="125" y1="80" x2="125" y2="76" stroke="#9333EA" strokeWidth="1" />
            <line x1="95" y1="50" x2="99" y2="50" stroke="#9333EA" strokeWidth="1" />
            
            <line x1="146" y1="29" x2="143" y2="32" stroke="#9333EA" strokeWidth="1" />
            <line x1="146" y1="71" x2="143" y2="68" stroke="#9333EA" strokeWidth="1" />
            <line x1="104" y1="71" x2="107" y2="68" stroke="#9333EA" strokeWidth="1" />
            <line x1="104" y1="29" x2="107" y2="32" stroke="#9333EA" strokeWidth="1" />
            
            {/* Rotating hands with CSS animation classes */}
            <line 
              x1="125" y1="50" x2="125" y2="30" 
              stroke="#9333EA" 
              strokeWidth="2.5" 
              strokeLinecap="round"
              className="origin-center animate-spin"
              style={{ transformOrigin: '125px 50px', animationDuration: '6s' }}
            />
            
            <line 
              x1="125" y1="50" x2="145" y2="50" 
              stroke="#9333EA" 
              strokeWidth="1.5" 
              strokeLinecap="round"
              className="origin-center animate-spin"
              style={{ transformOrigin: '125px 50px', animationDuration: '1s' }}
            />
            
            {/* Test paper */}
            <g className="animate-pulse" style={{ animationDuration: '3s' }}>
              <rect x="175" y="30" width="40" height="55" rx="3" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="1" />
              
              {/* Question content lines */}
              <rect x="180" y="40" width="30" height="2" rx="1" fill="#60A5FA" />
              <rect x="180" y="45" width="25" height="2" rx="1" fill="#60A5FA" />
              <rect x="180" y="50" width="28" height="2" rx="1" fill="#60A5FA" />
              
              {/* Multiple choice options */}
              <circle cx="183" cy="60" r="2" fill="#3B82F6" />
              <rect x="188" y="59" width="20" height="2" rx="1" fill="#60A5FA" />
              
              <circle cx="183" cy="66" r="2" fill="#3B82F6" />
              <rect x="188" y="65" width="18" height="2" rx="1" fill="#60A5FA" />
              
              <circle cx="183" cy="72" r="2" fill="#3B82F6" />
              <rect x="188" y="71" width="22" height="2" rx="1" fill="#60A5FA" />
            </g>
            
            {/* Floating elements */}
            <circle cx="30" cy="20" r="8" fill="#FDE68A" className="animate-pulse" style={{ animationDuration: '2.5s' }} />
            <circle cx="190" cy="15" r="5" fill="#FBCFE8" className="animate-pulse" style={{ animationDuration: '3.2s' }} />
            <rect x="160" y="87" width="10" height="10" rx="2" fill="#A7F3D0" className="animate-pulse" style={{ animationDuration: '2.8s' }} />
            <rect x="70" y="15" width="8" height="8" rx="2" fill="#C7D2FE" className="animate-pulse" style={{ animationDuration: '3s' }} />
          </svg>
          
          {/* Loading spinner */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-70 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
            <div className="mr-3 h-4 w-4">
              <svg className="animate-spin h-full w-full text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <span className="text-indigo-800 font-medium">{loadingText}</span>
          </div>
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 mb-3">{heading}</h2>
        <p className="text-gray-600 mb-6">{subheading}</p>
        
        {/* Loading progress indicator */}
        {/* <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
          <div className="relative bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 h-2 w-3/4 rounded-full"> */}
            {/* Shimmer effect */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
          </div>
        </div> */}
        <span className="text-sm text-gray-500">{bottomText}</span>
      </div>
    </div>
  );
};

export default LoadingComponent;
